import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Axios from "../../services/AxiosInstance";

// Create HR: Admin-side mirror of the Partner signup page
// (VedMGPartnerUI SignUp.jsx + SignUpValidation, same markup/classes,
// same fields, same order, same messages). Submits the same payload keys
// to POST api/admin/hr-list, which batch-creates business + HR login
// exactly like Partner registerBusiness.
// Two deliberate deviations, both Admin-context only:
//   - no CryptoJS password encryption (the Admin API has no login_key;
//     the Admin JWT + x-api-key + HTTPS protect transport, bcrypt
//     server-side like Partner);
//   - success goes to HR LIST with a toast (there is no OTP/verify_otp
//     flow for Admin-created accounts).
const CreateHrSchema = Yup.object().shape({
  businessName: Yup.string("BusinessName must be in String")
    .max(50)
    .required("Please Enter Your Business Name"),
  businessWebsite: Yup.string()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      "Enter correct url!"
    )
    .max(200)
    .required("Please Enter Your Business Website URL"),
  name: Yup.string("Name must be in String")
    .max(40)
    .required("Please Enter Your Name"),
  email: Yup.string()
    .email("That doesn't look like a Email")
    .max(100)
    .required("Email is required"),
  phoneNum: Yup.string()
    .test(
      "len",
      "Must be exactly 10 digits",
      (val) => val && val.toString().length === 10
    )
    .required("A phone number is required"),
  password: Yup.string().min(8).max(20).required("Password is required"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .min(8)
    .max(20)
    .required("Password is required"),
  description: Yup.string().max(500),
  isAgree: Yup.boolean().oneOf(
    [true],
    "You must accept the terms and conditions"
  ),
});

const CreateHr = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const callCreateHrApi = async (values) => {
    setSubmitting(true);
    setServerError("");
    try {
      const response = await Axios.post("api/admin/hr-list", {
        business_name: values.businessName,
        business_contact_person: values.name,
        business_email_id: values.email,
        business_mobile_no: values.phoneNum,
        business_website: values.businessWebsite,
        password: values.password,
        description: values.description,
      });
      if (response.data && response.data.code === "success") {
        showToast("HR created successfully");
        setTimeout(() => navigate("/hr-list"), 600);
      } else {
        setServerError(
          (response.data && response.data.message) ||
            "Failed to create HR. Please retry."
        );
      }
    } catch (err) {
      setServerError(
        err?.response?.data?.message || "Failed to create HR. Please retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const { handleSubmit, values, handleChange, errors, handleBlur, touched } =
    useFormik({
      initialValues: {
        businessName: "",
        businessWebsite: "",
        name: "",
        email: "",
        phoneNum: "",
        description: "",
        password: "",
        confirm_password: "",
        isAgree: false,
      },
      onSubmit: callCreateHrApi,
      validationSchema: CreateHrSchema,
    });

  return (
    <>
      <div className="content-page create-hr-page">
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="create-hr-title-block">
                  <div className="create-hr-title-row">
                    <h2 className="create-hr-title">Create HR</h2>
                    <button
                      className="btn btn-gradient btn-block"
                      type="button"
                      onClick={() => navigate("/create-employee")}
                    >
                      Create Employee
                    </button>
                  </div>
                  <p className="create-hr-sub">
                    Register a new business and its HR user in one step
                  </p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 col-lg-6 col-xl-6 mx-auto">
                <div className="card bg-pattern rigister-form create-hr-scope">
                  <div className="card-div p-4">
                    <div className="text-center w-75 m-auto admin_lg ">
                    </div>
                    {serverError && (
                      <div className="alert alert-danger text-center">
                        {serverError}
                      </div>
                    )}
                    <form onSubmit={handleSubmit}>
                      <div className="form-group mb-3 rigister-colm">
                        <label htmlFor="business-name">Business Name</label>
                        <input
                          onChange={handleChange}
                          name="businessName"
                          id="business-name"
                          maxLength={51}
                          value={values.businessName}
                          type="text"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Enter Business name"
                        />
                        {touched.businessName && errors.businessName && (
                          <div className="invalid-input">
                            {errors.businessName}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-colm">
                        <label htmlFor="busines-website">Business Website</label>
                        <input
                          onChange={handleChange}
                          name="businessWebsite"
                          id="busines-website"
                          maxLength={201}
                          value={values.businessWebsite}
                          type="text"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Business Website"
                        />
                        {touched.businessWebsite && errors.businessWebsite && (
                          <div className="invalid-input">
                            {errors.businessWebsite}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-colm">
                        <label htmlFor="your-name">Your Name</label>
                        <input
                          onChange={handleChange}
                          name="name"
                          id="your-name"
                          value={values.name}
                          maxLength={41}
                          type="text"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Your Name"
                        />
                        {touched.name && errors.name && (
                          <div className="invalid-input">{errors.name}</div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-colm">
                        <label htmlFor="email-address">Your Email</label>
                        <input
                          onChange={handleChange}
                          name="email"
                          id="email-address"
                          value={values.email}
                          maxLength={101}
                          type="email"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Enter Email Address"
                        />
                        {touched.email && errors.email && (
                          <div className="invalid-input">{errors.email}</div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-phn">
                        <label htmlFor="phone-number">Your Phone Number</label>
                        <input
                          onChange={handleChange}
                          name="phoneNum"
                          id="phone-number"
                          maxLength={11}
                          value={values.phoneNum}
                          type="tel"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Enter Phone Number"
                        />
                        {touched.phoneNum && errors.phoneNum && (
                          <div className="invalid-input">
                            {errors.phoneNum}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-colm">
                        <label htmlFor="new-password">New Password </label>
                        <input
                          id="new-password"
                          maxLength={21}
                          onChange={handleChange}
                          name="password"
                          onBlur={handleBlur}
                          type="password"
                          placeholder="Enter Your Password"
                          value={values.password}
                          className="form-control"
                        />
                        {touched.password && errors.password && (
                          <div className="invalid-input">
                            {errors.password}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-3 rigister-colm">
                        <label>Confirm Password </label>
                        <input
                          type="password"
                          onChange={handleChange}
                          maxLength={21}
                          name="confirm_password"
                          onBlur={handleBlur}
                          value={values.confirm_password}
                          className="form-control"
                          placeholder="Confirm Your Password"
                        />
                        {touched.confirm_password &&
                          errors.confirm_password && (
                            <div className="invalid-input">
                              {errors.confirm_password}
                            </div>
                          )}
                      </div>

                      <div className="form-group mb-3 rigister-textarea">
                        <label htmlFor="discription">Description</label>
                        <textarea
                          onChange={handleChange}
                          name="description"
                          id="discription"
                          maxLength={501}
                          value={values.description}
                          type="text"
                          onBlur={handleBlur}
                          className="form-control"
                          placeholder="Write something about your business in 250 words"
                        />
                      </div>

                      <div className="form-group">
                        <div className="custom-control custom-checkbox checkbox-success">
                          <input
                            onChange={handleChange}
                            name="isAgree"
                            value={values.isAgree}
                            type="checkbox"
                            onBlur={handleBlur}
                            className="custom-control-input"
                            id="checkbox-signup"
                          />
                          <label
                            className="custom-control-label"
                            htmlFor="checkbox-signup"
                          >
                            I accept{" "}
                            <a className="text-dark">
                              Terms and Conditions
                            </a>
                          </label>
                        </div>
                        {touched.isAgree && errors.isAgree && (
                          <div className="invalid-input">{errors.isAgree}</div>
                        )}
                      </div>

                      <div className="form-group mb-0 text-center">
                        <button
                          className="btn btn-gradient btn-block"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting ? "Creating..." : "Create HR"}
                        </button>
                      </div>
                    </form>
                  </div>
                  {/* <!-- end card-div --> */}
                </div>
                {/* <!-- end card --> */}
              </div>
              {/* <!-- end col --> */}
            </div>
            {/* <!-- end row --> */}
          </div>
          {/* <!-- end container --> */}
        </div>
        {/* <!-- end page --> */}
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "65px",
            right: "20px",
            background: "#28a745",
            color: "white",
            padding: "12px 20px",
            borderRadius: "6px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            zIndex: 2000,
          }}
        >
          {toast}
        </div>
      )}
    </>
  );
};

export default CreateHr;
